import { AgNumberValueCellEditingRenderer } from 'components/AgGrid/AgNumberValueCellEditingRenderer';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { TextWithTooltip } from 'components/WithTooltip';
import { BadgeWithTooltip } from 'components/WithTooltip/BadgeWithTooltip';
import type {
    AddRatioToActKc2StrategyEnum,
    WorkOrderChangeOtherPriceData,
    WorkOrderChangeOtherSeasonPriceData,
    WorkOrderChangePriceData,
    WorkOrderChangePriceItem,
    WorkOrderChangePriceSeasonValue,
    WorkOrderChangeSeasonPriceData,
    WorkOrderVersionPriceData,
} from 'gql/graphql';
import { DeviationEnum, OtherCostCalculationTypeEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { CodeCell } from 'pages/Project/Common/CodeCell/CodeCell';
import type { AnyObject } from 'types/AnyObject';
import { Badge, Checkbox, cnMixSpace, IconAlert, IconWarning, TextField } from 'uikit';
import { formatNumberToRu } from 'utils';
import { DeviatedCell } from 'utils/tableCells';

import type { CellClassRules, GridApi, ICellRendererParams, RowClassRules } from 'ag-grid-community';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { useUpdateWorkOrderChangePlanVolume } from '../hooks/useUpdateWorkOrderChangePlanVolume';
import { WorkOrderChangeRowMenu } from '../WorkOrderChangeRowMenu';
import { useWorkOrderChangeRowDataRefresh } from '../WorkOrderChangeTable/hooks/useWorkOrderChangeRowDataRefresh';

import Styles from './WorkOrderChangeVolumeEdit.module.css';

export const WorkOrderChangeStatusRenderer = ({ data }: { data: WorkOrderChangePriceItem }) => {
    if (data?.type === 'ROOT_OBJECT') {
        return '';
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {data?.hasLimitSpan ? (
                <TooltipWrapper message="Добавлено за счёт распределения лимитов" isInteractive={false}>
                    <Badge size="s" status="system" label="Л" />
                </TooltipWrapper>
            ) : (
                <div />
            )}
            {data?.status && <CodeCell status={data.status} />}
        </div>
    );
};

const styleBadge = {
    width: '16px',
    height: '16px',
    fontSize: '10px',
    lineHeight: '16px',
    padding: 0,
    justifyContent: 'center',
};

interface IWorkOrderChangeMenuRenderer {
    workOrderChangeUerRules?: IUseAccess['workOrderChangeUer'];
    checkRowHandler: ({ id, data }: { id: number; data: AnyObject | null }) => void;
    checkSingleRowHandler?: ({ id, data }: { id: number; data: AnyObject | null }) => void;
    checkedRows: Record<number, AnyObject>;
    openSingleDeleteModalHandler?: () => void;
    openAddMtrModalHandler?: () => void;
    openAddRatioModalHandler?: (strategy: AddRatioToActKc2StrategyEnum | null) => void;
    openEditDistributionModalHandler?: () => void;
    openEditOtherModalHandler?: () => void;
    openViewOtherModalHandler?: () => void;
    openEditRatioModal?: () => void;
    openEditMtrModalHandler?: () => void;
    openEditUerModalHandler?: () => void;
    openEditNoteModalHandler?: () => void;
    isActionsDisabled: boolean;
    setRowData?: (data: any) => void;
    openMoveVolumesBetweenSeasonsForUer?: () => void;
    isOpenMoveVolumesBetweenSeasonsForUerDisabled?: boolean;
    openMatchMtrDialogHandler?: () => void;
}

export const WorkOrderChangeMenuRenderer =
    (props: IWorkOrderChangeMenuRenderer) =>
    // eslint-disable-next-line react/display-name
    ({ data }: { data: WorkOrderChangePriceData }) => {
        const {
            checkRowHandler,
            checkedRows,
            checkSingleRowHandler,
            openSingleDeleteModalHandler,
            openAddMtrModalHandler,
            openAddRatioModalHandler,
            openEditDistributionModalHandler,
            openEditOtherModalHandler,
            openViewOtherModalHandler,
            openEditRatioModal,
            openEditMtrModalHandler,
            openEditNoteModalHandler,
            openEditUerModalHandler,
            isActionsDisabled,
            workOrderChangeUerRules,
            setRowData,
            openMoveVolumesBetweenSeasonsForUer,
            isOpenMoveVolumesBetweenSeasonsForUerDisabled,
            openMatchMtrDialogHandler,
        } = props;

        if (data === undefined || !data?.type) {
            return null;
        }

        const handleCheckbox =
            (id: number) =>
            ({ checked }: { checked: boolean }) => {
                if (checked) {
                    checkRowHandler({ id, data });
                } else {
                    checkRowHandler({ id, data: null });
                }
            };

        const kebabDisabled = !!data.isExcludedPrice || isActionsDisabled || !!data.isBlockedPrice;

        const checkboxRemoveDisabled =
            data.hasAcceptedCost !== null || kebabDisabled || !workOrderChangeUerRules?.canEdit;

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                    checked={!!checkedRows[data.clientId]}
                    onChange={handleCheckbox(data.clientId)}
                    disabled={checkboxRemoveDisabled}
                />
                <WorkOrderChangeRowMenu
                    rowData={data}
                    openSingleDeleteModalHandler={openSingleDeleteModalHandler}
                    checkSingleRowHandler={checkSingleRowHandler}
                    openAddMtrModalHandler={openAddMtrModalHandler}
                    openAddRatioModalHandler={openAddRatioModalHandler}
                    openEditOtherDistributionHandler={openEditDistributionModalHandler}
                    openEditOtherModalHandler={openEditOtherModalHandler}
                    openViewOtherModalHandler={openViewOtherModalHandler}
                    openEditRatioModal={openEditRatioModal}
                    openEditUerModal={openEditUerModalHandler}
                    openEditMtrModalHandler={openEditMtrModalHandler}
                    openEditNoteModalHandler={openEditNoteModalHandler}
                    setRowData={setRowData}
                    isDisabled={kebabDisabled || !workOrderChangeUerRules?.canEdit}
                    openMoveVolumesBetweenSeasonsForUer={openMoveVolumesBetweenSeasonsForUer}
                    isOpenMoveVolumesBetweenSeasonsForUerDisabled={isOpenMoveVolumesBetweenSeasonsForUerDisabled}
                    openMatchMtrDialogHandler={openMatchMtrDialogHandler}
                />
            </div>
        );
    };

const ACT_PRICE_TEXT = 'Добавлено на этапе приемки';
const W_O_CHANGE_PRICE_TEXT = 'Добавлено из Изм. к НЗ';

export const WorkOrderChangeCodeRenderer = ({ data }: { data: any }) => {
    const isUer = data?.type === 'UER';
    const isOther = data?.type === 'OTHERS' || data?.type === 'OTHER';

    const cellContent = isUer ? <span style={{ fontWeight: '600' }}>{data?.code}</span> : <span>{data?.code}</span>;
    const leftMarginItemClass = !(isUer || isOther) ? cnMixSpace({ mL: 'xl' }) : '';
    const rightMarginItemClass = !isOther ? cnMixSpace({ mR: 'xs' }) : '';

    const tooltipAddedText = useMemo(() => {
        if (data?.rationale?.length) {
            return (
                <div className={Styles.StyleRationale}>
                    {data?.isActKC2Price && <span className={Styles.textAdditions}>{ACT_PRICE_TEXT}</span>}
                    {data?.isAddedPrice && <span className={Styles.textAdditions}>{W_O_CHANGE_PRICE_TEXT}</span>}
                    <p>{data?.rationale}</p>
                </div>
            );
        }

        if (data?.isActKC2Price) {
            return <span className={Styles.textAdditions}>{ACT_PRICE_TEXT}</span>;
        }

        if (data?.isAddedPrice) {
            return <span className={Styles.textAdditions}>{W_O_CHANGE_PRICE_TEXT}</span>;
        }

        return '';
    }, [data]);

    if (tooltipAddedText) {
        return (
            <div
                className={leftMarginItemClass}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <ComponentWithTooltip
                    tooltipText={tooltipAddedText}
                    styles={{ display: 'flex', alignItems: 'center' }}
                    className={rightMarginItemClass}
                >
                    {data?.isActKC2Price || data?.isAddedPrice ? (
                        <Badge label="Д" status="success" size="s" style={styleBadge} />
                    ) : null}
                </ComponentWithTooltip>
                {cellContent}
            </div>
        );
    }

    return <div className={leftMarginItemClass}>{cellContent}</div>;
};
export const WorkOrderChangeHeaderCellWithTooltip =
    ({ text, tooltipText }: { text: string; tooltipText: string }) =>
    // eslint-disable-next-line react/display-name
    () => <TextWithTooltip text={text} tooltipText={tooltipText} view="secondary" />;

export const WorkOrderChangeNumericCell =
    ({
        key,
        decimal = 2,
        maxDecimal,
        ignoreRoot,
        ignoreOther,
        ignoreOtherGroup,
    }: {
        key: keyof WorkOrderChangePriceData;
        decimal?: number;
        maxDecimal?: number;
        ignoreRoot?: boolean;
        ignoreOther?: boolean;
        ignoreOtherGroup?: boolean;
    }) =>
    ({ data }: { data: any }) => {
        const value = data === undefined || !data?.[key] ? 0 : data?.[key];

        if (ignoreRoot && data?.type === 'ROOT_OBJECT') {
            return null;
        }
        if (ignoreOther && data?.type === 'OTHER') {
            return null;
        }
        if (ignoreOtherGroup && data?.type === 'OTHER_GROUP') {
            return null;
        }

        if (data?.isGroupTitle) {
            return null;
        }

        return formatNumberToRu(value, decimal, maxDecimal);
    };

export const WorkOrderChangeSeasonValueGetter =
    ({
        seasonId,
        dataKey,
        seasonYear,
    }: {
        seasonYear?: number;
        seasonId: number;
        dataKey:
            | keyof WorkOrderChangeSeasonPriceData
            | keyof WorkOrderChangeOtherSeasonPriceData
            | keyof WorkOrderChangePriceSeasonValue;
    }) =>
    ({ data }: { data: WorkOrderChangePriceData | WorkOrderChangeOtherPriceData | WorkOrderChangePriceItem }) => {
        let currentSeason = {} as any;

        if (data && 'seasonPrices' in data) {
            currentSeason = (data?.seasonPrices as WorkOrderChangeOtherSeasonPriceData[])?.find(
                (seasonItem) => seasonItem?.workOrderChangeSeasonId === seasonId || seasonItem?.year === seasonYear,
            );
        } else if (data && 'seasonValues' in data) {
            currentSeason = data?.seasonValues?.find(
                (seasonItem) =>
                    seasonItem?.workOrderChangeSeasonId === seasonId || seasonItem?.seasonYear === seasonYear,
            );
        }

        const currentSeasonValue = currentSeason?.[dataKey];
        const seasonWithErrors = currentSeason && 'hasErrors' in currentSeason && currentSeason?.hasErrors;
        const errorMsg = currentSeason && 'hasErrors' in currentSeason && currentSeason?.errors;
        const volumeEditDisabled = data?.isExcludedPrice;
        const deviation = currentSeason?.deviation;
        const planVolumeTerms =
            (currentSeason as WorkOrderChangePriceSeasonValue)?.planTerms ??
            (currentSeason as WorkOrderChangeSeasonPriceData)?.planVolumeTerms;

        return {
            value: currentSeasonValue,
            volumeEditDisabled,
            deviation,
            seasonWithErrors,
            errorMsg,
            planVolumeTerms,
        };
    };

export const WorkOrderChangeSeasonValueRenderer =
    ({
        decimal = 2,
        maxDecimal,
        withDeviation = false,
        withErrorCheck,
        ignoreRoot,
        ignoreOther,
        ignoreOtherGroup,
    }: {
        decimal?: number;
        maxDecimal?: number;
        ignoreRoot?: boolean;
        ignoreOther?: boolean;
        ignoreOtherGroup?: boolean;
        withDeviation?: boolean;
        withErrorCheck?: boolean;
    }) =>
    // eslint-disable-next-line react/display-name
    (params: ICellRendererParams) => {
        const { data, getValue } = params;
        const { value = 0, deviation, seasonWithErrors, errorMsg } = getValue?.() ?? {};

        if (data?.isGroupTitle) {
            return null;
        }
        if (data?.type === 'ROOT_OBJECT' && ignoreRoot) {
            return null;
        }
        if (ignoreOtherGroup && data?.type === 'OTHER_GROUP') {
            return null;
        }
        if ((data?.type === 'OTHER' || data?.type === 'OTHER_GROUP') && ignoreOther) {
            return null;
        }

        const formattedValue = formatNumberToRu(value, decimal, maxDecimal);

        if (seasonWithErrors && withErrorCheck) {
            return (
                <div style={{ display: 'flex' }}>
                    <BadgeWithTooltip
                        messages={errorMsg}
                        size="l"
                        badgeSize="s"
                        label="Ошибка"
                        status="error"
                        iconLeft={IconWarning}
                    />
                </div>
            );
        }

        return withDeviation && deviation === DeviationEnum.Up ? (
            <DeviatedCell margin={5}>{formattedValue}</DeviatedCell>
        ) : (
            formattedValue
        );
    };

export const WorkOrderChangeSeasonVolumeEditRenderer = (
    params: ICellRendererParams & {
        seasonId: number;
        getVariablesChangeVolume: (data: any, workOrderChangeSeasonId: number, value: number) => AnyObject;
        checkIsEditable: (data: any) => boolean;
        decimal?: number;
        maxDecimal?: number;
        workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
        apiGrid: GridApi;
        constructionTypeId: number;
        workOrderChangeId: number;
        refreshTableObjectsProp?: (objectIds: number[], needUpdateOnlyObj: boolean) => void;
    },
) => {
    const {
        seasonId,
        getVariablesChangeVolume,
        checkIsEditable,
        decimal = 3,
        maxDecimal,

        value: d,
        getValue,
        column,
        data,
        workOrderChangeUerRules,
        apiGrid,
        workOrderChangeId,
        constructionTypeId,
        refreshTableObjectsProp,
        ...rest
    } = params;

    const { value = '', planVolumeTerms = [] } = getValue?.() || {};
    const volumeEditDisabled = !checkIsEditable(data ?? {});

    const tooltipMessage = useMemo(
        () =>
            planVolumeTerms?.length > 1
                ? planVolumeTerms.reduce((acc: string, p: number, idx: number) => `${acc} ${idx ? '+' : ''} ${p}`, '')
                : undefined,
        [planVolumeTerms],
    );

    const isCoefficient =
        data?.type === WorkOrderPriceTypeEnum.Coefficient ||
        data?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient ||
        data?.type === WorkOrderPriceTypeEnum.TechPartCoefficient ||
        data?.type === WorkOrderPriceTypeEnum.DismantlingCoefficient;

    const { handleUpdateVolume } = useUpdateWorkOrderChangePlanVolume({
        useAgCellRowDataRefresh: useWorkOrderChangeRowDataRefresh,
        useAgCellRowDataRefreshParams: {
            workOrderChangeId,
            constructionTypeId,
            isSMRItem: true,
            isRootObject: !!data?.workOrderChangeRootObjectId,
        },
        api: apiGrid,
        workOrderChangeRootObjectId: data?.workOrderChangeRootObjectId,
    });

    const onChangeHandler = useCallback(
        (newInputValue: number | null) => {
            let workOrderChangeObjUpdater;

            if (!data?.workOrderChangeRootObjectId || !data?.workOrderChangeObjectId) {
                workOrderChangeObjUpdater = () => {
                    refreshTableObjectsProp?.([data?.workOrderObjectId ?? data?.workOrderRootObjectId], true);
                };
            }

            handleUpdateVolume(getVariablesChangeVolume(data, seasonId, newInputValue ?? 0), workOrderChangeObjUpdater);
        },
        [data, getVariablesChangeVolume, handleUpdateVolume, refreshTableObjectsProp, seasonId],
    );

    if (data?.isGroupTitle || data?.type === 'OTHER' || data?.type === 'ROOT_OBJECT' || data?.type === 'OTHER_GROUP') {
        return null;
    }

    if (data?.calculatingType === OtherCostCalculationTypeEnum.Calculation || isCoefficient) {
        return formatNumberToRu(value || 0, decimal, maxDecimal);
    }

    return (
        <AgNumberValueCellEditingRenderer
            isEditable
            idKey="id"
            columnKey=""
            data={data}
            value={value}
            decimalScale={decimal}
            onChange={onChangeHandler}
            debounceTime={750}
            width="full"
            size="xs"
            disabled={volumeEditDisabled || !workOrderChangeUerRules?.canEdit}
            className={cn({ [Styles.negativeValue]: value < 0 })}
            tooltipMessage={tooltipMessage}
            showTooltipOnHover
            {...rest}
        />
    );
};

export const WorkOrderChangePassageHeaderComponent = (props: any) => {
    const { displayName } = props;

    return (
        <div style={{ display: 'flex' }}>
            <TooltipWrapper toolTipClassName="toolTipWidth" message="Имеются незаполненые обязательные поля">
                <IconAlert size="s" view="alert" />
            </TooltipWrapper>
            <span style={{ marginLeft: 5 }}>{displayName}</span>
        </div>
    );
};

export const WorkOrderChangePassageCellRenderer = () => <TextField size="xs" status="alert" disabled />;

export const WorkOrderChangeIsEditableCheck =
    (isActionDisabled: boolean) =>
    ({
        data,
    }: {
        data: {
            type?: WorkOrderPriceTypeEnum;
            isExcludedPrice?: boolean;
            isBlockedPrice?: boolean;
            calculatingType?: OtherCostCalculationTypeEnum;
        };
    }) =>
        !isActionDisabled &&
        !(data?.type === WorkOrderPriceTypeEnum.Coefficient) &&
        !(data?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient) &&
        !(data?.type === WorkOrderPriceTypeEnum.TechPartCoefficient) &&
        !data?.isExcludedPrice &&
        !data?.isBlockedPrice &&
        !(data?.calculatingType === OtherCostCalculationTypeEnum.Calculation);

export const getRowClass: RowClassRules = {
    groupTitleClass: ({ data }: { data: { isGroupTitle: boolean } }) =>
        data?.isGroupTitle || (data as any)?.type === 'ROOT_OBJECT',
    grayRow: ({ data }: { data: { isGroupTitle: boolean } }) => (data as any)?.type === 'OTHER_GROUP',
    transparentRow: ({ data }: { data: { isExcludedPrice: boolean; isBlockedPrice: boolean } }) =>
        data?.isExcludedPrice || data?.isBlockedPrice,
    emptyVolumes: ({ data }: { data: { hasEmptySeasonVolume?: boolean; hasEmptySeasonCost?: boolean } }) =>
        (!!data?.hasEmptySeasonVolume || !!data?.hasEmptySeasonCost) && (data as any)?.type !== 'ROOT_OBJECT',
    COEFFICIENT: ({ data }: { data: WorkOrderVersionPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.Coefficient ||
        data?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient ||
        data?.type === WorkOrderPriceTypeEnum.TechPartCoefficient,
    MTR_CONTRACTOR_MATERIAL: ({ data }: { data: WorkOrderVersionPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrContractorMaterial,
    MTR_CUSTOMER_MATERIAL: ({ data }: { data: WorkOrderVersionPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrCustomerMaterial,
    MTR_CONTRACTOR_EQUIPMENT: ({ data }: { data: WorkOrderVersionPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrContractorEquipment,
    MTR_CUSTOMER_EQUIPMENT: ({ data }: { data: WorkOrderVersionPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrCustomerEquipment,
};

export const getCellClass: CellClassRules = {
    negativeValue: 'x < 0 || x?.value < 0',
};

WorkOrderChangeStatusRenderer.displayName = 'WorkOrderChangeStatusRenderer';
WorkOrderChangeMenuRenderer.displayName = 'WorkOrderChangeMenuRenderer';
WorkOrderChangeCodeRenderer.displayName = 'WorkOrderChangeCodeRenderer';
WorkOrderChangeHeaderCellWithTooltip.displayName = 'WorkOrderChangeHeaderCellWithTooltip';
WorkOrderChangeNumericCell.displayName = 'WorkOrderChangeNumericCell';
WorkOrderChangeSeasonValueGetter.displayName = 'WorkOrderChangeSeasonValueGetter';
WorkOrderChangeSeasonValueRenderer.displayName = 'WorkOrderChangeSeasonValueRenderer';
WorkOrderChangeSeasonVolumeEditRenderer.displayName = 'WorkOrderChangeSeasonVolumeEditRenderer';
WorkOrderChangePassageHeaderComponent.displayName = 'WorkOrderChangePassageHeaderComponent';
WorkOrderChangePassageCellRenderer.displayName = 'WorkOrderChangePassageCellRenderer';
